import React, { useMemo, useCallback, useEffect, useRef, useState } from "react";
import { ITournamentState } from "../../../../Core/State/ILobbyTournamentState";
import { IUserState } from "../../../../Core/State/IUserState";
import { isMobile } from "react-device-detect";
import { Box, Button, Stack, SvgIcon, SvgIconProps, Tooltip, Typography, IconButton, Popover, CircularProgress, TextField, InputAdornment } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import { InteractionHandler } from "../../../../Core/Handler/InteractionHandler";
import TicketPrizesTooltip from "./TicketPrizesTooltip";
import ReactCountryFlag from "react-country-flag";
import { getCountryName } from "../../../../Utils/CountryUtils";
import { t } from '../../../../i18n';
import {
    formatCurrency,
    formatTimeDifference,
    isLateRegistrationOpen,
    getEligibleTickets,
    getStartTimer,
    getStartPercentage,
    getStartTextColour,
    formatChips,
    getStartTextClean,
    getStartTextOnly,
    getPrizeCount,
    canRegisterForTournament,
    getTournamentRoomActivePlayerCount,
    isTerminalTournamentStatus,
    isTournamentRegisteredByUser
} from "../../../../Utils/TournamentUtils";
import TournamentExtraInfo from "../../../GameRoom/SubComponents/TournamentExtraInfo";
import TournamentRegisterButton from "./TournamentRegisterButton";
import defaultTourneyImg from '/images/tournament/default.png';
import tourneyLobbyBg from "/images/tournament/lobby-background.png";
import tourneyLobbyMobileBg from "/images/tournament/lobby-mobile-background.png";
import tableEmpty from "/images/tournament/table-empty.png";
import tourneyIcon from "/images/tournament/trophy-icon.svg";
import ticketIcon from "/images/tournament/ticket-icon.svg";
import SquareIcon from '@mui/icons-material/Square';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

function TrophyIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 18 19">
        <path d="M10.5714 13.7515C10.5714 13.5675 10.4257 13.4548 10.2476 13.5011C10.2476 13.5011 9.41349 13.7179 8.79787 13.7179C8.18225 13.7179 7.34812 13.5011 7.34812 13.5011C7.17002 13.4548 7.02429 13.5675 7.02429 13.7515V14.7219C7.02429 14.9059 7.17489 15.0565 7.35888 15.0565H10.2369C10.4209 15.0565 10.5714 14.9059 10.5714 14.7219V13.7515Z"/>
        <path d="M13.8892 1.26941C13.8892 0.899813 13.5895 0.600098 13.2199 0.600098H4.38538C4.01578 0.600098 3.71606 0.899813 3.71606 1.26941V6.791C3.71606 8.51927 4.2301 10.0925 5.16345 11.2207C6.10296 12.3564 7.3954 12.9819 8.80263 12.9819C10.2099 12.9819 11.5023 12.3565 12.4418 11.2207C13.3752 10.0925 13.8892 8.51927 13.8892 6.791V1.26941Z"/>
        <path d="M3.07614 8.13034C2.16026 7.78952 1.73852 6.99905 1.73852 5.685V2.6955H2.97673V1.35693H1.06934C0.699673 1.35693 0.400024 1.65658 0.400024 2.02625V5.68494C0.400024 7.91696 1.46914 9.27658 3.43497 9.59418C3.27346 9.12971 3.15252 8.6397 3.07614 8.13034Z"/>
        <path d="M4.38538 16.0596C4.01578 16.0596 3.71606 16.2095 3.71606 16.3945V18.0523C3.71606 18.2372 4.01578 18.3872 4.38538 18.3872H13.22C13.5896 18.3872 13.8893 18.2372 13.8893 18.0523V16.3945C13.8893 16.2095 13.5896 16.0596 13.22 16.0596H4.38538Z"/>
        <path d="M16.5305 1.35742H14.623V2.69599H15.8612V5.68543C15.8612 6.99948 15.4395 7.78994 14.5236 8.13076C14.4472 8.64013 14.3264 9.13019 14.1648 9.59467C16.1306 9.27707 17.1997 7.91745 17.1997 5.68543V2.02674C17.1998 1.65707 16.9002 1.35742 16.5305 1.35742Z"/>
    </SvgIcon>
  );
}

function UserIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 17 18">
        <path d="M8.41724 9.32921C6.0605 9.32921 4.14351 6.82792 4.14351 4.47139C4.14351 2.11485 6.0605 0.197266 8.41724 0.197266C10.774 0.197266 12.6912 2.11485 12.6912 4.47139C12.6908 6.82773 10.7738 9.32921 8.41724 9.32921Z"/>
        <path d="M5.20894 9.37305C6.03795 10.0047 7.07215 10.3804 8.19242 10.3804H8.64232C9.76279 10.3804 10.7972 10.0045 11.626 9.37305C14.3329 9.81063 16.3999 12.1568 16.3999 14.9869C16.3999 16.5416 12.8261 17.8024 8.41737 17.8024C4.00886 17.8024 0.434839 16.5416 0.434839 14.9869C0.434839 12.1568 2.50207 9.81063 5.20894 9.37305Z"/>
    </SvgIcon>
  );
}

function WinIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 33 25">
        <path d="M13.8063 5.99742C10.0593 9.89613 13.8063 12.7227 16.7055 10.8569C16.5248 11.8685 16.1125 12.8226 15.5021 13.6417H18.7705C18.0074 12.9116 17.5229 11.93 17.403 10.8708C20.4389 12.6392 23.6253 9.54803 20.2611 5.98349C18.9706 4.84741 17.8792 3.49607 17.0337 1.9873C16.1322 3.46004 15.0474 4.80791 13.8063 5.99742Z"/>
        <path d="M28.7942 4.09956C29.5681 1.00772 26.7303 0.144531 26.7303 0.144531C26.7303 0.144531 25.8158 2.96586 28.7942 4.09956Z"/>
        <path d="M31.896 2.92578C31.896 2.92578 29.0578 3.78861 29.8306 6.8801C32.8094 5.74783 31.896 2.92578 31.896 2.92578Z"/>
        <path d="M29.9583 10.6586C33.0752 9.99306 32.6017 7.06445 32.6017 7.06445C32.6017 7.06445 29.6655 7.48528 29.9583 10.6586Z"/>
        <path d="M28.8794 14.067C32.0552 14.3365 32.4531 11.3967 32.4531 11.3967C32.4531 11.3967 29.5216 10.9454 28.8794 14.067Z"/>
        <path d="M26.9058 17.3837C29.8943 18.4923 31.0635 15.7661 31.0635 15.7661C31.0635 15.7661 28.3591 14.5477 26.9058 17.3837Z"/>
        <path d="M23.9336 19.6149C26.3829 21.6548 28.3927 19.4728 28.3927 19.4728C28.3927 19.4728 26.248 17.4243 23.9336 19.6149Z"/>
        <path d="M20.7043 21.3966C22.7059 23.8768 25.1046 22.1314 25.1046 22.1314C25.1046 22.1314 23.403 19.7018 20.7043 21.3966Z"/>
        <path d="M17.7407 22.0228C19.0057 24.9482 21.784 23.9081 21.784 23.9081C21.784 23.9081 20.7945 21.1119 17.7407 22.0228Z"/>
        <path d="M29.0744 6.91422C29.0034 3.72802 26.0382 3.64551 26.0382 3.64551C26.0382 3.64551 25.9022 6.60855 29.0744 6.91422Z"/>
        <path d="M26.6024 6.85254C26.6024 6.85254 25.9448 9.74419 29.0126 10.6067C29.5063 7.45814 26.6024 6.85254 26.6024 6.85254Z"/>
        <path d="M27.9805 13.8641C29.102 10.881 26.3815 9.69922 26.3815 9.69922C26.3815 9.69922 25.151 12.3975 27.9805 13.8641Z"/>
        <path d="M26.0903 16.8782C27.8802 14.2412 25.5131 12.4546 25.5131 12.4546C25.5131 12.4546 23.6837 14.7887 26.0903 16.8782Z"/>
        <path d="M24.0162 14.5864C24.0162 14.5864 21.7301 16.4757 23.6348 19.0312C25.9467 16.8373 24.0162 14.5864 24.0162 14.5864Z"/>
        <path d="M21.8002 15.999C21.8002 15.999 19.2053 17.4348 20.6066 20.297C23.283 18.5671 21.8002 15.999 21.8002 15.999Z"/>
        <path d="M19.5589 17.3237C19.5589 17.3237 16.725 18.1973 17.51 21.2863C20.4842 20.1418 19.5589 17.3237 19.5589 17.3237Z"/>
        <path d="M6.69695 0.144043C6.69695 0.144043 3.85912 1.00723 4.63298 4.09908C7.61145 2.96573 6.69695 0.144043 6.69695 0.144043Z"/>
        <path d="M3.59634 6.8801C4.36912 3.78825 1.53092 2.92578 1.53092 2.92578C1.53092 2.92578 0.617504 5.74783 3.59634 6.8801Z"/>
        <path d="M3.46916 10.6586C3.76191 7.48528 0.82577 7.06445 0.82577 7.06445C0.82577 7.06445 0.352199 9.9927 3.46916 10.6586Z"/>
        <path d="M4.54754 14.067C3.90535 10.9454 0.973877 11.3967 0.973877 11.3967C0.973877 11.3967 1.37175 14.3361 4.54754 14.067Z"/>
        <path d="M6.52126 17.3837C5.0679 14.5477 2.36353 15.7661 2.36353 15.7661C2.36353 15.7661 3.53238 18.4923 6.52126 17.3837Z"/>
        <path d="M5.03418 19.4728C5.03418 19.4728 7.04399 21.6548 9.49328 19.6149C7.17852 17.4243 5.03418 19.4728 5.03418 19.4728Z"/>
        <path d="M12.7225 21.3966C10.0239 19.7018 8.32227 22.1314 8.32227 22.1314C8.32227 22.1314 10.721 23.8768 12.7225 21.3966Z"/>
        <path d="M11.6431 23.9081C11.6431 23.9081 14.4213 24.9485 15.6864 22.0228C12.6329 21.1119 11.6431 23.9081 11.6431 23.9081Z"/>
        <path d="M7.38951 3.64551C7.38951 3.64551 4.42431 3.72802 4.35327 6.91422C7.52512 6.60855 7.38951 3.64551 7.38951 3.64551Z"/>
        <path d="M6.8246 6.85254C6.8246 6.85254 3.92075 7.45814 4.41441 10.6067C7.48257 9.74419 6.8246 6.85254 6.8246 6.85254Z"/>
        <path d="M7.04493 9.69922C7.04493 9.69922 4.32441 10.881 5.44592 13.8641C8.27586 12.3975 7.04493 9.69922 7.04493 9.69922Z"/>
        <path d="M7.33669 16.8782C9.74329 14.7887 7.91395 12.4546 7.91395 12.4546C7.91395 12.4546 5.54681 14.2412 7.33669 16.8782Z"/>
        <path d="M9.41152 14.5864C9.41152 14.5864 7.481 16.8377 9.79288 19.0312C11.6976 16.4757 9.41152 14.5864 9.41152 14.5864Z"/>
        <path d="M12.8203 20.297C14.2217 17.4344 11.6267 15.999 11.6267 15.999C11.6267 15.999 10.1439 18.5671 12.8203 20.297Z"/>
        <path d="M13.8683 17.3237C13.8683 17.3237 12.943 20.1415 15.9172 21.2863C16.7022 18.1973 13.8683 17.3237 13.8683 17.3237Z"/>
    </SvgIcon>
  );
}

interface TournamentLobbyContentProps {
    tournament: ITournamentState | null;
    userState: IUserState;
    interactionHandler: InteractionHandler
}

const renderTournamentCountryFlag = (countryCode?: string) => {
    if (!countryCode) return null;

    return (
        <Tooltip title={getCountryName(countryCode)} placement="top" arrow slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [0, -8] } }] } }}>
            <span style={{ display: 'inline-block', marginRight: '6px', verticalAlign: '1px' }}>
                <ReactCountryFlag
                    countryCode={countryCode}
                    svg
                    style={{ width: '20px', height: '14px', flexShrink: 0, borderRadius: '2px' }}
                />
            </span>
        </Tooltip>
    );
};

const TournamentLobbyContent: React.FC<TournamentLobbyContentProps> = ({
    tournament,
    userState,
    interactionHandler
}) => {
    if (!tournament) return null;
    const [tab, setTab] = useState(0);
    const [rightTab, setRightTab] = useState(0);
    const [extraInfoAnchorEl, setExtraInfoAnchorEl] = useState<HTMLElement | null>(null);
    const [actionCooldown, setActionCooldown] = useState(false);
    const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Player search/filter state
    const [playerSearch, setPlayerSearch] = useState('');
    const filteredPlayerList = useMemo(() => {
        const list = tournament.playerList ?? [];
        const q = (playerSearch || '').trim().toLowerCase();
        if (!q) return list;
        return list.filter(p => (p.username || '').toLowerCase().includes(q));
    }, [tournament.playerList, playerSearch]);
    // Rankings search/filter state
    const [rankingsSearch, setRankingsSearch] = useState('');
    const filteredRankings = useMemo(() => {
        const list = tournament.rankings ?? [];
        const q = (rankingsSearch || '').trim().toLowerCase();
        if (!q) return list;
        return list.filter(r => (r.username || '').toLowerCase().includes(q));
    }, [tournament.rankings, rankingsSearch]);

    const runWithCooldown = useCallback((action: () => void) => {
        if (actionCooldown) return;

        setActionCooldown(true);
        action();
        cooldownTimerRef.current = setTimeout(() => {
            setActionCooldown(false);
            cooldownTimerRef.current = null;
        }, 2000);
    }, [actionCooldown]);

    const handleExtraInfoOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setExtraInfoAnchorEl(event.currentTarget);
    }, []);

    const handleExtraInfoClose = useCallback(() => {
        setExtraInfoAnchorEl(null);
    }, []);

    const handlePopoverMouseLeave = useCallback(() => {
        setExtraInfoAnchorEl(null);
    }, []);

    useEffect(() => {
        return () => {
            if (cooldownTimerRef.current) {
                clearTimeout(cooldownTimerRef.current);
            }
        };
    }, []);

    // Get current time on each render for accurate countdowns
    const currentTime = new Date();
    const startTime = useMemo(() => new Date(tournament.startTime), [tournament.startTime]);
    const isTournamentRunning = useMemo(() => {
        const status = tournament.status;
        if (status === 'running' || status === 'paused') {
            return true;
        }
        return false;
    }, [tournament.status]);
    const hasTournamentStarted = useMemo(() => {
        const status = tournament.status;
        if (status === 'running' || status === 'paused') {
            return true;
        }
        const nowMs = currentTime.getTime();
        const startMs = startTime.getTime();
        return nowMs >= startMs && status !== 'scheduled';
    }, [tournament.status, startTime, currentTime]);
    const entryMode = tournament.registrationEntryMode || 'cash_only';
    const cashAllowed = entryMode === 'cash_only' || entryMode === 'cash_or_ticket';
    const ticketAllowed = entryMode === 'ticket_only' || entryMode === 'cash_or_ticket';
    const eligibleTickets = useMemo(
        () => getEligibleTickets(userState.tickets || [], tournament),
        [userState.tickets, tournament]
    );
    const hasEligibleTicket = eligibleTickets.length > 0;
    const showTicketButton = ticketAllowed && hasEligibleTicket;
    const showTicketDisabled = ticketAllowed && !hasEligibleTicket && entryMode === 'ticket_only';

    const formatDateTime = useCallback((dateInput: string | Date) => {
        const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
        if (!date || isNaN(date.getTime())) return 'Invalid Date';
        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'short' });
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day} ${month}, ${hours}:${minutes}`;
    }, []);

    const formatTimeDifferenceLocal = useCallback((targetTime: Date, currentTime: Date = new Date()): string => {
        return formatTimeDifference(targetTime, currentTime);
    }, []);

    const getStatusColor = useMemo(() => {
        if (currentTime < startTime) {
            const diffMs = startTime.getTime() - currentTime.getTime();
            if (diffMs < 3600000) {
                return '#1CDC56';
            } else {
                return '#FFC100';
            }
        }
        if (tournament.status === 'running') {
            return '#3772FE';
        } else if (tournament.status === 'cancelled') {
            return '#D12B3C';
        } else if (tournament.status === 'completed') {
            return '#FFFFFF';
        }
        return '#FFFFFF';
    }, [currentTime, startTime, tournament.status]);

    const formatCurrencyLocal = useCallback((amount: number) => {
        return formatCurrency(amount);
    }, []);

    // Calculate registration window times
    const registrationTimes = useMemo(() => {
        const startTime = new Date(tournament.startTime);

        // Registration start time
        const registrationStart = new Date(startTime);
        if (tournament.registrationDurationMinutes) {
            registrationStart.setMinutes(registrationStart.getMinutes() - tournament.registrationDurationMinutes);
        }

        // Late registration end time
        const lateRegistrationEnd = new Date(startTime);
        if (tournament.lateRegistrationMinutes) {
            lateRegistrationEnd.setMinutes(lateRegistrationEnd.getMinutes() + tournament.lateRegistrationMinutes);
        } else {
            return { registrationStart, registrationEnd: startTime, lateRegistrationEnd: null };
        }

        return { registrationStart, registrationEnd: startTime, lateRegistrationEnd };
    }, [tournament.startTime, tournament.registrationDurationMinutes, tournament.lateRegistrationMinutes]);

    const getRegistrationStatusText = useMemo(() => {
        const { registrationStart, registrationEnd, lateRegistrationEnd } = registrationTimes;

        const isValidDate = (d: unknown): d is Date => d instanceof Date && !isNaN(d.getTime());

        if (!isValidDate(registrationStart) || !isValidDate(registrationEnd)) {
            return t('Tournament:status.closed');
        }

        if (currentTime < registrationStart) {
            // Registration hasn't started yet
            return `Opens in ${formatTimeDifferenceLocal(registrationStart, currentTime)}`;
        }

        if (currentTime < registrationEnd) {
            // Regular registration window
            return `Open (${formatTimeDifferenceLocal(registrationEnd, currentTime)})`;
        }

        if (lateRegistrationEnd && isLateRegistrationOpen(tournament, currentTime)) {
            // Late registration window
            return `${t('Tournament:status.lateRegistration')} ${formatTimeDifferenceLocal(lateRegistrationEnd, currentTime)}`;
        }

        return t('Tournament:status.closed');
    }, [registrationTimes, currentTime, formatTimeDifferenceLocal, tournament]);

    const isUserRegistered = useMemo(
        () => isTournamentRegisteredByUser(tournament, userState.id),
        [tournament, userState.id]
    );
    const handleRegisterClick = useCallback(() => {
        runWithCooldown(() => interactionHandler.signUpForTournament(tournament.gameId));
    }, [interactionHandler, tournament.gameId, runWithCooldown]);

    const handleUnregisterClick = useCallback(() => {
        runWithCooldown(() => interactionHandler.unsignFromTournament(tournament.gameId));
    }, [interactionHandler, tournament.gameId, runWithCooldown]);

    const handleJoinTournamentRoom = useCallback((room: any, event?: React.MouseEvent) => {
        // This lobby can be rendered inside a detached window (React portal). Pass the window where the
        // click occurred so InteractionHandler can preserve user activation for popup reservations/focus.
        const sourceWindow =
            (event?.currentTarget as any)?.ownerDocument?.defaultView ||
            ((event?.nativeEvent as any)?.view as (Window | null | undefined));
        interactionHandler.joinRoom(room.roomId, undefined, sourceWindow);
    }, [interactionHandler]);

    const handleRegisterWithTicket = useCallback(() => {
        const ticketId = eligibleTickets[0]?._id;
        if (!ticketId) return;
        runWithCooldown(() => interactionHandler.registerForTournamentWithTicket(tournament.gameId, ticketId));
    }, [eligibleTickets, interactionHandler, runWithCooldown, tournament.gameId]);

    // Generate prize structure array with position and prize amount
    const getPrizeStructureArray = () => {
        if (!(tournament as any).payoutStructure || (tournament as any).payoutStructure.length === 0) {
            return [];
        }

        const playerCount = tournament.registeredPlayerCount;
        const applicableStructure = (tournament as any).payoutStructure.find(
            (structure: any) => playerCount >= structure.minPlayers && playerCount <= structure.maxPlayers
        );

        if (!applicableStructure) {
            return [];
        }

        const totalPrize = tournament.guaranteedPrizePool && tournament.guaranteedPrizePool > tournament.prizePool
            ? tournament.guaranteedPrizePool
            : tournament.prizePool;

        return applicableStructure.payouts.map((payout: any) => {
            const amount = (totalPrize * payout.percentage) / 100;
            return {
                position: payout.position,
                prizeAmount: amount,
                percentage: payout.percentage
            };
        });
    };

    // Helper function to check if late registration is open
    const isLateRegistrationOpenLocal = (t: ITournamentState, now: Date = new Date()): boolean => {
        return isLateRegistrationOpen(t, now);
    };

    const canRegisterNow = canRegisterForTournament(tournament, new Date());
    const hasSpace = tournament.registeredPlayerCount < tournament.maxPlayers;

    // Unregister should only show if user is registered AND tournament hasn't started
    const canUnregister = isUserRegistered && new Date() < new Date(tournament.startTime);
    const tournamentStatusLabel = useMemo(() => {
        const stateColors: Record<string, string> = {
            running: '#6BD2FF',
            registration: '#1BE958',
            late_registration: '#F93751',
            scheduled: '#1BE958',
            starting: '#1BE958',
            completed: '#16EFC1',
            cancelled: '#F93751',
            paused: '#FF9800',
        };

        const isLate = isLateRegistrationOpenLocal(tournament, new Date());
        let displayText: string;
        let color: string;

        if (isTerminalTournamentStatus(tournament.status)) {
            displayText = tournament.status === 'completed' ? t('Tournament:status.completed') : t('Tournament:status.cancelled');
            color = stateColors[tournament.status] || '#FFFFFF';
        } else if (isUserRegistered && (tournament.status !== 'running' && tournament.status !== 'paused')) {
            displayText = t('Tournament:box.registered');
            color = '#6BD2FF';
        } else if (tournament.status === 'paused') {
            displayText = t('Tournament:status.onBreak');
            color = '#FF9800';
        } else if (isLate) {
            displayText = t('Tournament:status.lateRegistration');
            color = '#F93751';
        } else {
            const statusTranslations: Record<string, string> = {
                running: t('Tournament:status.running'),
                registration: t('Tournament:status.registration'),
                late_registration: t('Tournament:status.lateRegistration'),
                scheduled: t('Tournament:status.registration'),
                starting: t('Tournament:status.registering'),
                completed: t('Tournament:status.completed'),
                cancelled: t('Tournament:status.cancelled'),
                paused: t('Tournament:status.onBreak'),
            };
            displayText = statusTranslations[tournament.status] || tournament.status;
            color = stateColors[tournament.status] || '#FFFFFF';
        }

        return { displayText, color };
    }, [isUserRegistered, tournament]);
    const popoverBlindLevel = tournament.tournamentExtraInformation?.blindLevel;
    const currentBlindLevelDisplay = (
        typeof popoverBlindLevel?.currentLevel === 'number' &&
        typeof popoverBlindLevel.currentSmallBlind === 'number' &&
        typeof popoverBlindLevel.currentBigBlind === 'number'
    )
        ? {
            level: popoverBlindLevel.currentLevel,
            smallBlind: popoverBlindLevel.currentSmallBlind,
            bigBlind: popoverBlindLevel.currentBigBlind
        }
        : tournament.currentBlindLevel;
    const currentLevel = currentBlindLevelDisplay?.level;
    const showStructureAnte = tournament.type !== 'texas';
    const hasMinPlayers = typeof tournament.minPlayers === 'number' && tournament.minPlayers > 0;

    const getTableName = (name: string): string => {
        const match = name.match(/Table \d+$/);
        return match ? match[0] : name;
    };

    return (
        <Stack direction="column" sx={{ p: isMobile ? 1 : 2, height: '100%', backgroundColor: isMobile ? '#15151E' : '#22232A', boxSizing: 'border-box', gap: isMobile ? 1.5 : 2 }}>
            {/* Top row */}
            <Stack direction="row" sx={{ height: isMobile ? 'initial' : '255px', backgroundColor: '#3B3C45', borderRadius: isMobile ? '4px' : 'initial' }}>
                <Box sx={{ flexGrow: 1, width: isMobile ? '100%' : 'initial' }}>
                    <Stack direction={isMobile ? 'column' : 'row'} sx={{ backgroundImage: `url(${isMobile ? tourneyLobbyMobileBg : tourneyLobbyBg})`, backgroundRepeat: 'no-repeat', backgroundSize: 'cover', px: 3, py: 2, boxSizing: 'border-box', gap: 3 }}>
                        <Box style={{ textAlign: `${isMobile ? 'center' : 'initial'}` }}>
                            <img src={tournament.imageName ? `${import.meta.env.BASE_URL}images/tournament/${tournament.imageName}` : defaultTourneyImg} alt="Tournament Logo" height="65" />
                        </Box>
                        <Stack direction="column" sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: '600', color: '#ffffff', fontSize: '20px', lineHeight: '28px', mb: 1, textAlign: isMobile ? 'center' : 'initial' }}>
                                {tournament.templateName || tournament.name}
                                {tournament.tournamentExtraInformation && (
                                    <>
                                        <IconButton
                                            size="small"
                                            onMouseEnter={handleExtraInfoOpen}
                                            sx={{ color: '#FFFFFF', ml: 1, verticalAlign: 'top' }}
                                        >
                                            <InfoIcon fontSize="small" />
                                        </IconButton>
                                        <Popover
                                            open={Boolean(extraInfoAnchorEl)}
                                            anchorEl={extraInfoAnchorEl}
                                            onClose={handleExtraInfoClose}
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                                            PaperProps={{
                                                onMouseLeave: handlePopoverMouseLeave,
                                                sx: {
                                                    backgroundColor: 'transparent',
                                                    boxShadow: 'none',
                                                    maxHeight: 360,
                                                    overflow: 'auto'
                                                }
                                            }}
                                        >
                                            <TournamentExtraInfo
                                                inline
                                                hasStarted={hasTournamentStarted}
                                                tournamentExtraInformation={tournament.tournamentExtraInformation}
                                                prizePool={tournament.prizePool}
                                                guaranteedPrizePool={tournament.guaranteedPrizePool}
                                            />
                                        </Popover>
                                    </>
                                )}
                            </Typography>
                            <Stack direction="row" sx={{ gap: 1 }} justifyContent={isMobile ? 'center' : 'initial'}>
                                <Box sx={{ color: tournamentStatusLabel.color, fontWeight: 500, fontSize: '12px', backgroundColor: `${tournamentStatusLabel.color}33`, borderRadius: '3px', padding: '2px 6px', width: 'fit-content', textTransform: 'capitalize', fontFamily: `'Inter', sans-serif` }}>
                                    {tournamentStatusLabel.displayText}
                                </Box>
                                <Box sx={{ backgroundColor: tournament.type === 'aof' ? '#57222E' : '#2B5056', color: tournament.type === 'aof' ? '#FC5468' : '#4DDAA2', height: '18px', lineHeight: '20px', fontSize: '12px', fontWeight: 500, padding: '0 0.75rem', borderRadius: '20px', fontFamily: `'Inter', sans-serif` }}>
                                    {tournament.type === 'aof' ? 'AoF' : (tournament.type === 'texas' ? t('Tournament:gameTypes.holdem') : tournament.type.toUpperCase())}
                                </Box>
                            </Stack>
                        </Stack>
                    </Stack>
                    {!isMobile && (
                        <Box sx={{ borderBottom: '2px solid #D43655', mx: 3, position: 'relative' }}>
                            <SquareIcon style={{ color: '#D43655', transform: 'rotate(45deg)', fontSize: '14px', position: 'absolute', top: '-6px', left: '-3px' }}></SquareIcon>
                        </Box>
                    )}
                    <Stack direction="row" sx={{ px: 3, py: 2, boxSizing: 'border-box' }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ position: 'relative', gap: { xs: 2, lg: 4 }, mt: { xs: 0, sm: 4, md: 0 } }}>
                            <Box sx={{ width: '3px', background: '#FFDB98', height: isMobile ? '45px' : '63px', borderRadius: '2px', position: 'absolute', left: '-24px', boxShadow: '0 -10px 30px 0 rgba(255, 194, 0, 0.80)' }}></Box>
                            <img src={tourneyIcon} alt="Trophy Icon" height={isMobile ? 22 : 32} width={isMobile ? 20 : 29} />
                            <Stack>
                                <Typography sx={{ color: '#8695A7', fontSize: isMobile ? '12px' : { xs: '14px', lg: '16px' }, fontWeight: 500 }}>{t('Tournament:info.prizePool')}</Typography>
                                <Typography sx={{
                                    color: '#FFDB98',
                                    background: '-webkit-linear-gradient(#FFDB98, #FFFFFF)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    fontSize: isMobile ? '16px' : { xs: '22px', lg: '26px' },
                                    fontWeight: 600
                                }}>
                                    {formatCurrencyLocal(tournament.prizePool)}
                                </Typography>
                            </Stack>
                            <Stack>
                                <Typography sx={{ color: '#8695A7', fontSize: isMobile ? '12px' : { xs: '14px', lg: '16px' }, fontWeight: 500 }}>{t('Tournament:info.buyIn')}</Typography>
                                <Typography sx={{ color: '#FFFFFF', fontSize: isMobile ? '14px' : { xs: '16px', lg: '18px' }, fontWeight: 600, lineHeight: isMobile ? '24px' : { xs: '33px', lg: '39px'} }}>
                                    {tournament.buyIn === 0 ? t('Tournament:gameTypes.freeroll') : `${formatCurrencyLocal(tournament.buyIn)} + ${formatCurrencyLocal(tournament.entryFee)}`}
                                </Typography>
                            </Stack>
                            {tournament.guaranteedPrizePool ? (
                                <Stack>
                                    <Typography sx={{ color: '#8695A7', fontSize: isMobile ? '12px' : { xs: '14px', lg: '16px' }, fontWeight: 500 }}>{t('Tournament:info.guaranteed')}</Typography>
                                    <Typography sx={{ color: '#FFFFFF', fontSize: isMobile ? '14px' : { xs: '16px', lg: '18px' }, fontWeight: 600, lineHeight: isMobile ? '24px' : { xs: '33px', lg: '39px'} }}>
                                        {formatCurrencyLocal(tournament.guaranteedPrizePool)}
                                    </Typography>
                                </Stack>
                            ) : null}
                        </Stack>
                        {!isMobile && (
                            <Stack sx={{ width: '265px', borderLeft: '1px solid rgba(255, 255, 255, 0.15)', ml: 'auto', pl: 3, display: { xs: 'none', md: 'flex' } }}>
                                <Typography variant="body1" sx={{ display: 'flex', mb: 0.5, pb: 0.5, borderBottom: '1px solid rgba(255, 255, 255, 0.15)' }}>
                                    <span style={{ color: '#8695A7', width: '50%', textAlign: 'left' }}>{t('Tournament:info.entries')}</span>
                                    <span style={{ marginLeft: 'auto', color: '#ffffff', width: '50%', fontWeight: 'bold', textAlign: 'right' }}>{tournament.registeredPlayerCount} / {tournament.maxPlayers}</span>
                                </Typography>
                                {hasMinPlayers && (
                                    <Typography variant="body1" sx={{ display: 'flex', mb: 0.5, pb: 0.5, borderBottom: '1px solid rgba(255, 255, 255, 0.15)' }}>
                                        <span style={{ color: '#8695A7', width: '50%', textAlign: 'left' }}>{t('Tournament:info.minPlayers')}</span>
                                        <span style={{ marginLeft: 'auto', color: '#ffffff', width: '50%', fontWeight: 'bold', textAlign: 'right' }}>{tournament.minPlayers}</span>
                                    </Typography>
                                )}
                                {tournament.startingChips && (
                                    <Typography variant="body1" sx={{ display: 'flex', mb: 0.5, pb: 0.5, borderBottom: '1px solid rgba(255, 255, 255, 0.15)' }}>
                                        <span style={{ color: '#8695A7', width: '50%', textAlign: 'left' }}>{t('Tournament:info.startingChips')}</span>
                                        <span style={{ marginLeft: 'auto', color: '#ffffff', width: '50%', fontWeight: 'bold', textAlign: 'right' }}>{tournament.startingChips.toLocaleString()}</span>
                                    </Typography>
                                )}
                                {currentBlindLevelDisplay && (
                                    <Typography variant="body1" sx={{ display: 'flex', gap: 4 }}>
                                        <span style={{ color: '#8695A7', width: '35%', textAlign: 'left' }}>{t('Tournament:info.blindLevel')}</span>
                                        <span style={{ marginLeft: 'auto', color: '#ffffff', width: '65%', fontWeight: 'bold', textAlign: 'right' }}>Lvl {currentBlindLevelDisplay.level}: {formatChips(currentBlindLevelDisplay.smallBlind)}/{formatChips(currentBlindLevelDisplay.bigBlind)}</span>
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    </Stack>
                </Box>
                {!isMobile && (
                    <Box sx={{ backgroundColor: '#3F414A', height: '100%', aspectRatio: '1/1', p: 3, boxSizing: 'border-box', display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                        {(() => {
                            if (tournament.status === 'completed' || tournament.status === 'cancelled') return null;
                            if (hasTournamentStarted && (!isLateRegistrationOpen(tournament) || isUserRegistered)) return (
                                <Box sx={{ position: 'relative', height: "78px", mb: 2 }}>
                                    <img src={tableEmpty} alt="Tournament table" height={78} />
                                </Box>
                            );
                            return (
                                <Box sx={{ position: 'relative', height: "78px", mb: 2 }}>
                                    <CircularProgress variant="determinate" thickness={2} size="78px" value={100} sx={{ color: '#898B8A', position: 'absolute' }}></CircularProgress>
                                    <CircularProgress
                                        variant="determinate"
                                        thickness={2}
                                        size="78px"
                                        value={getStartPercentage(tournament)}
                                        sx={{
                                            color: getStartTextColour(tournament),
                                        }}
                                    />
                                    <Typography sx={{ textAlign: 'center', fontWeight: 600, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: getStartTextColour(tournament) }}>
                                        {getStartTimer(tournament)}
                                    </Typography>
                                </Box>
                            );
                        })()}
                        <Stack sx={{ width: '100%', height: '100%', boxSizing: 'border-box' }}>
                            {(() => {
                                if (tournament.status === 'completed' || tournament.status === 'cancelled') return (
                                    <Box sx={{ my: 'auto'}}>
                                        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>{t('Tournament:tableStructure.eventTypes.tournamentEnd')}</Typography>
                                        <Stack direction="row" justifyContent="space-between" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.15)', py: 0.5 }}>
                                            <Typography sx={{ color: '#8695A7', fontWeight: 500 }}>{t('Tournament:info.started')}</Typography>
                                            <Typography sx={{ fontWeight: 600 }}>{ formatDateTime(tournament.startTime) }</Typography>
                                        </Stack>
                                        {tournament.endTime && (
                                            <Stack direction="row" justifyContent="space-between" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.15)', py: 0.5 }}>
                                                <Typography sx={{ color: '#8695A7', fontWeight: 500 }}>{t('Tournament:status.completed')}</Typography>
                                                <Typography sx={{ fontWeight: 600 }}>{ formatDateTime(tournament.endTime) }</Typography>
                                            </Stack>
                                        )}
                                        <Stack direction="row" justifyContent="space-between" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.15)', py: 0.5 }}>
                                            <Typography sx={{ color: '#8695A7', fontWeight: 500 }}>{t('Tournament:info.prizes')}</Typography>
                                            <Typography sx={{ fontWeight: 600 }}>{ getPrizeCount(tournament) }</Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                                            <Typography sx={{ color: '#8695A7', fontWeight: 500 }}>{t('Tournament:info.entries')}</Typography>
                                            <Typography sx={{ fontWeight: 600 }}>{ tournament.registeredPlayerCount }</Typography>
                                        </Stack>
                                    </Box>
                                );
                                return (
                                    <>
                                        <Stack direction="row" justifyContent="space-between" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.15)', py: 0.5, mx: 1 }}>
                                            <Typography sx={{ color: getStartTextColour(tournament), fontWeight: 500 }}>{getStartTextOnly(tournament)}</Typography>
                                            <Typography sx={{ fontWeight: 600 }}>{ getStartTextClean(tournament) }</Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5, mx: 1 }}>
                                            <Typography sx={{ color: '#8695A7', fontWeight: 500 }}>{t('Tournament:info.players')}:</Typography>
                                            <Typography sx={{ fontWeight: 600 }}>{ tournament.registeredPlayerCount }</Typography>
                                        </Stack>
                                    </>
                                );
                            })()}
                        </Stack>
                        {(isTournamentRunning && ((!isLateRegistrationOpen(tournament) || isUserRegistered))) && (
                            <Button
                                variant="contained"
                                style={{ color: 'white', textTransform: 'capitalize', fontWeight: 700, fontSize: '16px' }}
                                sx={{ mt: 'auto' }}
                                onClick={() => setRightTab(1)}
                                fullWidth
                            >
                                {t('Tournament:info.tables')}
                            </Button>
                        )}
                        <TournamentRegisterButton
                            canRegisterNow={canRegisterNow}
                            canUnregister={canUnregister}
                            hasSpace={hasSpace}
                            cashAllowed={cashAllowed}
                            showTicketButton={showTicketButton}
                            showTicketDisabled={showTicketDisabled}
                            isUserRegistered={isUserRegistered}
                            actionCooldown={actionCooldown}
                            handleRegisterClick={handleRegisterClick}
                            handleRegisterWithTicket={handleRegisterWithTicket}
                            handleUnregisterClick={handleUnregisterClick}
                        />
                    </Box>
                )}
            </Stack>
            {isMobile && (
                <Stack direction="row" alignItems="center" sx={{ width: '100%', backgroundColor: '#3F414A', px: 2, py: 1, boxSizing: 'border-box', borderRadius: '4px' }}>
                    <Stack direction="row" alignItems="center" sx={{ width: '50%' }}>
                        <Typography sx={{ color: getStartTextColour(tournament), fontWeight: 500, fontSize: '14px' }}>{getStartTextOnly(tournament)}</Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: '16px', ml: 2, mr: 5 }}>{ getStartTextClean(tournament) }</Typography>
                    </Stack>
                    <TournamentRegisterButton
                        canRegisterNow={canRegisterNow}
                        canUnregister={canUnregister}
                        hasSpace={hasSpace}
                        cashAllowed={cashAllowed}
                        showTicketButton={showTicketButton}
                        showTicketDisabled={showTicketDisabled}
                        isUserRegistered={isUserRegistered}
                        actionCooldown={actionCooldown}
                        handleRegisterClick={handleRegisterClick}
                        handleRegisterWithTicket={handleRegisterWithTicket}
                        handleUnregisterClick={handleUnregisterClick}
                    />
                    {(() => {
                        if (isTournamentRunning && ((!isLateRegistrationOpen(tournament) || isUserRegistered))) return (
                            <Button
                                variant="contained"
                                style={{ color: 'white', textTransform: 'capitalize', fontWeight: 700, fontSize: '16px' }}
                                sx={{ mt: 'auto', maxWidth: '50%' }}
                                onClick={() => {setTab(2); setRightTab(1)}}
                                fullWidth
                            >
                                {t('Tournament:info.tables')}
                            </Button>
                        );
                        if (tournament.status === 'completed') return (
                            <Button
                                variant="contained"
                                style={{ color: 'white', textTransform: 'capitalize', fontWeight: 700, fontSize: '16px' }}
                                sx={{ mt: 'auto', maxWidth: '50%' }}
                                onClick={() => setTab(2)}
                                fullWidth
                            >
                                {t('Tournament:info.rankings')}
                            </Button>
                        );
                    })()}
                </Stack>
            )}
            {/* Bottom row */}
            <Stack direction={isMobile ? 'column' : 'row'} sx={{ height: isMobile ? 'calc(100% - 337px)' : 'calc(100% - 271px)', gap: isMobile ? 1 : 2, boxSizing: 'border-box' }}>
                {/* Left Column - Tournament Details */}
                {(tab === 0 || !isMobile) && (
                    <Stack direction="column" sx={{
                        width: isMobile ? '100%' : '33%',
                        height: '100%',
                        backgroundColor: '#3B3C45',
                        borderRadius: '4px',
                        boxSizing: 'border-box',
                        px: 2,
                        pt: 1,
                        pb: isMobile ? '60px' : 1
                    }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: '600', color: '#ffffff', fontSize: '16px', lineHeight: '28px', borderBottom: '1px solid rgba(255, 255, 255, 0.15)', width: '100%', pb: 1 }}>
                                {t('Tournament:info.info')}
                            </Typography>
                        </Box>
                        <Stack sx={{
                            maxHeight: 'calc(100% - 53px)',
                            overflow: 'auto',
                            '&::-webkit-scrollbar': {
                                width: '0.4em'
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#5A5A5A',
                                borderRadius: '4px'
                            }
                        }}>
                            <Typography variant="body1" sx={{ display: 'flex', mb: 1, gap: 4 }}>
                                <span style={{ color: '#8695A7', width: '40%', fontSize: '14px', fontWeight: 500 }}>{t('Tournament:info.startTime')}:</span>
                                <span style={{ marginLeft: 'auto', color: '#ffffff', width: '60%', fontSize: '16px', fontWeight: 600 }}>{formatDateTime(tournament.startTime)}</span>
                            </Typography>
                            <Typography variant="body1" sx={{ display: 'flex', mb: 1, gap: 4 }}>
                                <span style={{ color: '#8695A7', width: '40%', fontSize: '14px', fontWeight: 500 }}>{t('Tournament:info.status')}:</span>
                                <span style={{ marginLeft: 'auto', color: getStatusColor, width: '60%', fontSize: '16px', fontWeight: 600 }}>
                                    {currentTime < startTime ? `Starts in ${formatTimeDifferenceLocal(startTime, currentTime)}` :
                                        tournament.status === 'completed' ? t('Tournament:status.finished') :
                                            tournament.status === 'cancelled' ? 'Cancelled' : t('Tournament:status.running')}
                                </span>
                            </Typography>

                            <Typography variant="body1" sx={{ display: 'flex', mb: 1, gap: 4 }}>
                                <span style={{ color: '#8695A7', width: '40%', fontSize: '14px', fontWeight: 500 }}>{t('Tournament:info.buyIn')}:</span>
                                <span style={{ marginLeft: 'auto', color: '#ffffff', width: '60%', fontSize: '16px', fontWeight: 600 }}>{tournament.buyIn === 0 ? t('Tournament:gameTypes.freeroll') : `${formatCurrencyLocal(tournament.buyIn)} + ${formatCurrencyLocal(tournament.entryFee)}`}</span>
                            </Typography>

                            <Typography variant="body1" sx={{ display: 'flex', mb: 1, gap: 4 }}>
                                <span style={{ color: '#8695A7', width: '40%', fontSize: '14px', fontWeight: 500 }}>{t('Tournament:info.prizePool')}:</span>
                                <span style={{ marginLeft: 'auto', color: '#CC976D', width: '60%', fontSize: '16px', fontWeight: 600 }}>
                                    {formatCurrencyLocal(tournament.prizePool)}
                                    {tournament.ticketPrizes && tournament.ticketPrizes.length > 0 && (
                                        <TicketPrizesTooltip ticketPrizes={tournament.ticketPrizes}>
                                            <span style={{ cursor: 'help' }}>
                                                {tournament.ticketPrizes.length === 1 ? ' + ticket' : ' + tickets'}
                                            </span>
                                        </TicketPrizesTooltip>
                                    )}
                                </span>
                            </Typography>

                            {tournament.guaranteedPrizePool ? (
                                <Typography variant="body1" sx={{ display: 'flex', mb: 1, gap: 4 }}>
                                    <span style={{ color: '#8695A7', width: '40%', fontSize: '14px', fontWeight: 500 }}>{t('Tournament:info.guaranteed')}:</span>
                                    <span style={{ marginLeft: 'auto', color: '#CC976D', width: '60%', fontSize: '16px', fontWeight: 600 }}>{formatCurrencyLocal(tournament.guaranteedPrizePool)}</span>
                                </Typography>
                            ) : null}

                            <Typography variant="body1" sx={{ display: 'flex', mb: 1, gap: 4 }}>
                                <span style={{ color: '#8695A7', width: '40%', fontSize: '14px', fontWeight: 500 }}>{t('Tournament:info.players')}:</span>
                                <span style={{ marginLeft: 'auto', color: '#ffffff', width: '60%', fontSize: '16px', fontWeight: 600 }}>{tournament.registeredPlayerCount} / {tournament.maxPlayers}{hasMinPlayers ? ` (Min: ${tournament.minPlayers})` : ''}</span>
                            </Typography>

                            <Typography variant="body1" sx={{ display: 'flex', mb: 1, gap: 4 }}>
                                <span style={{ color: '#8695A7', width: '40%', fontSize: '14px', fontWeight: 500 }}>{t('Tournament:info.gameType')}:</span>
                                <span style={{ marginLeft: 'auto', color: '#ffffff', width: '60%', fontSize: '16px', fontWeight: 600 }}>{tournament.type === 'aof' ? 'AoF' : (tournament.type === 'texas' ? t('Tournament:gameTypes.holdem') : tournament.type.toUpperCase())}</span>
                            </Typography>

                            {tournament.startingChips && (
                                <Typography variant="body1" sx={{ display: 'flex', mb: 1, gap: 4 }}>
                                    <span style={{ color: '#8695A7', width: '40%', fontSize: '14px', fontWeight: 500 }}>{t('Tournament:info.startingChips')}:</span>
                                    <span style={{ marginLeft: 'auto', color: '#ffffff', width: '60%', fontSize: '16px', fontWeight: 600 }}>{tournament.startingChips.toLocaleString()}</span>
                                </Typography>
                            )}

                            {currentBlindLevelDisplay && (
                                <Typography variant="body1" sx={{ display: 'flex', gap: 4 }}>
                                    <span style={{ color: '#8695A7', width: '40%', fontSize: '14px', fontWeight: 500 }}>{t('Tournament:info.blindLevel')}:</span>
                                    <span style={{ marginLeft: 'auto', color: '#ffffff', width: '60%', fontSize: '16px', fontWeight: 600 }}>Level {currentBlindLevelDisplay.level}: {currentBlindLevelDisplay.smallBlind}/{currentBlindLevelDisplay.bigBlind}</span>
                                </Typography>
                            )}
                            {tournament.registrationDurationMinutes !== undefined && (
                                <>
                                    <Typography variant="body1" sx={{ display: 'flex', my: 1, gap: 4 }}>
                                        <span style={{ color: '#8695A7', width: '40%', fontSize: '14px', fontWeight: 500 }}>{t('Tournament:info.registration')}:</span>
                                        <span style={{ marginLeft: 'auto', color: '#ffffff', width: '60%', fontSize: '16px', fontWeight: 600 }}>
                                            {getRegistrationStatusText}
                                        </span>
                                    </Typography>
                                    <Typography variant="body1" sx={{ display: 'flex', mb: 1, gap: 4 }}>
                                        <span style={{ color: '#8695A7', width: '40%', fontSize: '14px', fontWeight: 500 }}>{t('Tournament:registration.opens')}:</span>
                                        <span style={{ marginLeft: 'auto', color: '#ffffff', width: '60%', fontSize: '16px', fontWeight: 600 }}>
                                            {formatDateTime(registrationTimes.registrationStart)}
                                        </span>
                                    </Typography>
                                    <Typography variant="body1" sx={{ display: 'flex', mb: 1, gap: 4 }}>
                                        <span style={{ color: '#8695A7', width: '40%', fontSize: '14px', fontWeight: 500 }}>{t('Tournament:registration.closes')}:</span>
                                        <span style={{ marginLeft: 'auto', color: '#ffffff', width: '60%', fontSize: '16px', fontWeight: 600 }}>
                                            {formatDateTime(registrationTimes.lateRegistrationEnd || registrationTimes.registrationEnd)}
                                        </span>
                                    </Typography>
                                </>
                            )}
                        </Stack>
                    </Stack>
                )}

                {/* Middle Column - Structure */}
                {(tab === 1 || !isMobile) && (
                    <Stack direction="column" sx={{
                        width: isMobile ? '100%' : '33%',
                        height: '100%',
                        backgroundColor: '#3B3C45',
                        borderRadius: '4px',
                        boxSizing: 'border-box',
                        px: 2,
                        pt: 1,
                        pb: isMobile ? '60px' : 1
                    }}>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: '600', color: '#ffffff', fontSize: '16px', lineHeight: '28px', borderBottom: '1px solid rgba(255, 255, 255, 0.15)', width: '100%', pb: 1 }}>
                                {t('Tournament:info.structure.title')}
                            </Typography>
                        </Box>
                        <Box sx={{ maxHeight: 'calc(100% - 53px)' }}>
                            {(tournament.blindStructureSnapshot && tournament.blindStructureSnapshot.length > 0) && (
                                <>
                                    <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.15)', pb: 1, mb: 2 }}>
                                        <Typography variant="body1" sx={{ width: showStructureAnte ? '15%' : '20%', color: '#8695A7', fontWeight: 500, textAlign: 'center' }}>{t('Tournament:info.structure.level')}</Typography>
                                        <Typography variant="body1" sx={{ width: showStructureAnte ? '35%' : '45%', color: '#8695A7', fontWeight: 500, textAlign: 'center' }}>{t('Tournament:info.structure.blinds')}</Typography>
                                        {showStructureAnte && (
                                            <Typography variant="body1" sx={{ width: '25%', color: '#8695A7', fontWeight: 500, textAlign: 'center' }}>{t('Tournament:info.structure.ante')}</Typography>
                                        )}
                                        <Typography variant="body1" sx={{ width: showStructureAnte ? '20%' : '30%', color: '#8695A7', fontWeight: 500, textAlign: 'center', paddingRight: '0.4em' }}>{t('Tournament:info.structure.interval')}</Typography>
                                    </Stack>
                                    <Stack sx={{
                                        gap: 1,
                                        height: 'calc(100% - 49px)',
                                        overflow: 'auto',
                                        '&::-webkit-scrollbar': {
                                            width: '0.4em'
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: '#5A5A5A',
                                            borderRadius: '4px'
                                        }
                                    }}>
                                        {tournament.blindStructureSnapshot.map((blind, index) => {
                                            const isCurrentLevel = blind.level === currentLevel;
                                            return (
                                                <Stack direction="row" key={blind.level} spacing={2}>
                                                    <Typography variant="body2" sx={{
                                                        width: showStructureAnte ? '15%' : '20%',
                                                        fontSize: '14px',
                                                        textAlign: 'center',
                                                        fontWeight: isCurrentLevel ? 'bold' : '500',
                                                        color: isCurrentLevel ? 'white' : '#8695A7'
                                                    }}>{blind.level}</Typography>
                                                    <Typography variant="body2" sx={{
                                                        width: showStructureAnte ? '35%' : '45%',
                                                        fontSize: '14px',
                                                        textAlign: 'center',
                                                        fontWeight: isCurrentLevel ? 'bold' : '500',
                                                        color: isCurrentLevel ? 'white' : '#8695A7'
                                                    }}>{formatChips(blind.smallBlind)} / {formatChips(blind.bigBlind)}</Typography>
                                                    {showStructureAnte && (
                                                        <Typography variant="body2" sx={{
                                                            width: '25%',
                                                            fontSize: '14px',
                                                            textAlign: 'center',
                                                            fontWeight: isCurrentLevel ? 'bold' : '500',
                                                            color: isCurrentLevel ? 'white' : '#8695A7'
                                                        }}>{blind.ante}</Typography>
                                                    )}
                                                    <Typography variant="body2" sx={{
                                                        width: showStructureAnte ? '20%' : '30%',
                                                        fontSize: '14px',
                                                        textAlign: 'center',
                                                        fontWeight: isCurrentLevel ? 'bold' : '500',
                                                        color: isCurrentLevel ? 'white' : '#8695A7'
                                                    }}>{blind.duration}</Typography>
                                                </Stack>
                                            );
                                        })}
                                    </Stack>
                                </>
                            )}
                        </Box>
                    </Stack>
                )}

                {/* Right Column - Players / Rankings / Tables */}
                {(tab === 2 || !isMobile) && (
                    <Stack direction="column" sx={{
                        width: isMobile ? '100%' : '33%',
                        height: '100%',
                        backgroundColor: '#3B3C45',
                        borderRadius: '4px',
                        boxSizing: 'border-box',
                        px: 2,
                        pt: 1,
                        pb: isMobile ? '60px' : 1
                    }}>
                        <Stack direction="row" gap={3} sx={{ mb: 1, borderBottom: '1px solid rgba(255, 255, 255, 0.15)' }}>
                            <Typography variant="h6" sx={{ fontWeight: '600', color: '#ffffff', fontSize: '16px', lineHeight: '28px', pb: 1, cursor: 'pointer', borderBottom: rightTab === 0 ? '1px solid white' : 'none' }} onClick={() => setRightTab(0)}>
                                {hasTournamentStarted ? t('Tournament:info.rankings') : t('Tournament:info.players')}
                            </Typography>
                            {tournament.activeRooms && tournament.activeRooms.length > 0 && (
                                <Typography variant="h6" sx={{ fontWeight: '600', color: '#ffffff', fontSize: '16px', lineHeight: '28px', pb: 1, cursor: 'pointer', borderBottom: rightTab === 1 ? '1px solid white' : 'none' }} onClick={() => setRightTab(1)}>
                                    {t('Tournament:info.tables')}
                                </Typography>
                            )}
                        </Stack>
                        {rightTab === 0 && (
                            <Box sx={{ maxHeight: 'calc(100% - 53px)' }}>
                                {(() => {
                                    // Show the tournament rankings
                                    if (tournament.rankings && tournament.rankings.length > 0) return (
                                        <>
                                            <TextField
                                                placeholder={t('Tournament:tableStructure.searchPlayer')}
                                                value={rankingsSearch}
                                                onChange={(e) => setRankingsSearch(e.target.value)}
                                                size="small"
                                                variant="outlined"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon sx={{ color: '#8695A7' }} />
                                                        </InputAdornment>
                                                    ),
                                                    sx: {
                                                        backgroundColor: '#464852',
                                                        borderRadius: '4px',
                                                        color: '#fff',
                                                        border: 'none',
                                                        '& .MuiInputBase-input': {
                                                            fontSize: '16px',
                                                            fontWeight: 500,
                                                            padding: '5px 14px 5px 0',
                                                            '&::placeholder': {
                                                                color: '#8695A7',
                                                                opacity: 1
                                                            }
                                                        }
                                                    }
                                                }}
                                                sx={{
                                                    mb: 1,
                                                    width: '100%',
                                                    "& .MuiOutlinedInput-root": {
                                                        "& fieldset": {
                                                            borderColor: "#464852",
                                                        },
                                                        "&:hover fieldset": {
                                                            borderColor: "#464852",
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            borderColor: "#464852",
                                                        },
                                                    },
                                                }}
                                            />
                                            <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.15)', pb: 1, mb: 2 }}>
                                                <Typography variant="body1" sx={{ width: '10%', color: '#8695A7', fontWeight: 500, pl: 1, boxSizing: 'border-box' }}>#</Typography>
                                                <Typography variant="body1" sx={{ width: '35%', color: '#8695A7', fontWeight: 500 }}>{t('Tournament:players.player')}</Typography>
                                                <Typography variant="body1" sx={{ width: '25%', color: '#8695A7', fontWeight: 500 }}>{t('Tournament:players.chips')}</Typography>
                                                <Typography variant="body1" sx={{ width: '30%', color: '#8695A7', fontWeight: 500 }}>{t('Tournament:info.prize')}</Typography>
                                            </Stack>
                                            <Stack sx={{
                                                gap: 1,
                                                height: 'calc(100% - 90px)',
                                                overflow: 'auto',
                                                '&::-webkit-scrollbar': {
                                                    width: '0.4em'
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    backgroundColor: '#5A5A5A',
                                                    borderRadius: '4px'
                                                }
                                            }}>
                                                {[...filteredRankings]
                                                    .sort((a, b) => {
                                                        // If positions are available, sort by position (ascending)
                                                        if ((a as any).position && (b as any).position) {
                                                            return (a as any).position - (b as any).position;
                                                        }
                                                        // Otherwise, sort by balance (descending)
                                                        return b.balance - a.balance;
                                                    })
                                                    .map((player, index) => {
                                                        const isEliminated = Boolean(player.eliminationTime);
                                                        const isHighlighted = player.userId === userState.id;
                                                        const isTopFive = index < 5;
                                                        const isWinner = index === 0 && tournament.status === 'completed';

                                                        // Calculate prize based on current position
                                                        const calculateCurrentPrize = () => {
                                                            // If tournament is completed and has prize data, use it
                                                            if ((player as any).prize) {
                                                                return (player as any).prize;
                                                            }

                                                            // Use actual payout structure if available
                                                            if ((tournament as any).payoutStructure && (tournament as any).payoutStructure.length > 0) {
                                                                const currentPosition = index + 1;
                                                                const playerCount = tournament.registeredPlayerCount;

                                                                // Find the applicable payout structure based on player count
                                                                const applicableStructure = (tournament as any).payoutStructure.find(
                                                                    (structure: any) => playerCount >= structure.minPlayers && playerCount <= structure.maxPlayers
                                                                );

                                                                if (applicableStructure) {
                                                                    // Find the payout for this position
                                                                    const positionPayout = applicableStructure.payouts.find(
                                                                        (payout: any) => payout.position === currentPosition
                                                                    );

                                                                    if (positionPayout) {
                                                                        // Use guaranteed prize pool if it's higher than current prize pool
                                                                        const totalPrize = tournament.guaranteedPrizePool && tournament.guaranteedPrizePool > tournament.prizePool
                                                                            ? tournament.guaranteedPrizePool
                                                                            : tournament.prizePool;
                                                                        return (totalPrize * positionPayout.percentage) / 100;
                                                                    }
                                                                }
                                                            }

                                                            return 0;
                                                        };

                                                        const currentPrize = calculateCurrentPrize();

                                                        return (
                                                            <Stack direction="row" key={player.userId} alignItems="center" sx={[
                                                                { gap: 2 },
                                                                isHighlighted && {
                                                                    position: 'sticky',
                                                                    bottom: 0,
                                                                    background: 'linear-gradient(90deg, rgba(245, 39, 64, 0.40) 12.71%, rgba(59, 60, 69, 0.40) 105.8%), #3B3C45',
                                                                    '&::before': {
                                                                        content: '""',
                                                                        position: 'absolute',
                                                                        left: 0,
                                                                        top: 0,
                                                                        bottom: 0,
                                                                        width: '4px',
                                                                        borderRadius: '2px',
                                                                        backgroundColor: '#F52740',
                                                                    }
                                                                },
                                                            ]}>
                                                                <Typography variant="body2" sx={{
                                                                    width: '10%',
                                                                    fontSize: '16px',
                                                                    textAlign: 'center',
                                                                    fontWeight: isHighlighted ? 'bold' : '600',
                                                                    color: isHighlighted ? '#ffffff' : '#8695A7'
                                                                }}>
                                                                    {((player as any).position || index + 1).toString()}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{
                                                                    width: '35%',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    fontSize: '14px',
                                                                    fontWeight: '600',
                                                                    color: isHighlighted ? '#ffffff' : '#8695A7'
                                                                }}>
                                                                    {renderTournamentCountryFlag(player.countryCode)}
                                                                    {player.username} {isHighlighted ? '(You)' : ''}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{
                                                                    width: '25%',
                                                                    fontSize: '14px',
                                                                    fontWeight: isHighlighted ? 'bold' : '600',
                                                                    color: isHighlighted ? '#ffffff' : '#8695A7'
                                                                }}>
                                                                    {!isEliminated && player.balance.toLocaleString() }
                                                                </Typography>
                                                                <Typography variant="body2" sx={{
                                                                    width: '30%',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    fontSize: '14px',
                                                                    fontWeight: isHighlighted ? 'bold' : '600',
                                                                    color: isHighlighted ? '#ffffff' : '#FFDB98'
                                                                }}>
                                                                    {(() => {
                                                                        const currentPosition = index + 1;
                                                                        const prizeText = currentPrize > 0 ? formatCurrencyLocal(currentPrize) : '';
                                                                        const hasTicket = tournament.ticketPrizes?.some(t => t.position === currentPosition);

                                                                        if (hasTicket) {
                                                                            return (
                                                                                <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5}>
                                                                                    {prizeText}
                                                                                    <TicketPrizesTooltip
                                                                                        ticketPrizes={tournament.ticketPrizes}
                                                                                        position={currentPosition}
                                                                                    >
                                                                                        <span style={{ cursor: 'help' }}>
                                                                                            <img src={ticketIcon} alt="Ticket Icon" />
                                                                                        </span>
                                                                                    </TicketPrizesTooltip>
                                                                                </Stack>
                                                                            );
                                                                        }

                                                                        return prizeText;
                                                                    })()}
                                                                </Typography>
                                                            </Stack>
                                                        );
                                                    }
                                                )}
                                            </Stack>
                                        </>
                                    );
                                    // If no rankings exist, show the player list
                                    if (tournament.playerList && tournament.playerList.length > 0) return (
                                        <>
                                            <TextField
                                                placeholder={t('Tournament:tableStructure.searchPlayer')}
                                                value={playerSearch}
                                                onChange={(e) => setPlayerSearch(e.target.value)}
                                                size="small"
                                                variant="outlined"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon sx={{ color: '#8695A7' }} />
                                                        </InputAdornment>
                                                    ),
                                                    sx: {
                                                        backgroundColor: '#464852',
                                                        borderRadius: '4px',
                                                        color: '#fff',
                                                        border: 'none',
                                                        '& .MuiInputBase-input': {
                                                            fontSize: '16px',
                                                            fontWeight: 500,
                                                            padding: '5px 14px 5px 0',
                                                            '&::placeholder': {
                                                                color: '#8695A7',
                                                                opacity: 1
                                                            }
                                                        }
                                                    }
                                                }}
                                                sx={{
                                                    mb: 1,
                                                    width: '100%',
                                                    "& .MuiOutlinedInput-root": {
                                                        "& fieldset": {
                                                            borderColor: "#464852",
                                                        },
                                                        "&:hover fieldset": {
                                                            borderColor: "#464852",
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            borderColor: "#464852",
                                                        },
                                                    },
                                                }}
                                            />
                                            <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.15)', pb: 1, mb: 2 }}>
                                                <Typography variant="body1" sx={{ width: '10%', color: '#8695A7', fontWeight: 500, pl: 1, boxSizing: 'border-box' }}>#</Typography>
                                                <Typography variant="body1" sx={{ width: '40%', color: '#8695A7', fontWeight: 500 }}>{t('Tournament:players.player')}</Typography>
                                                <Typography variant="body1" sx={{ width: '25%', color: '#8695A7', fontWeight: 500 }}>{t('Tournament:players.chips')}</Typography>
                                                <Typography variant="body1" sx={{ width: '25%', color: '#8695A7', fontWeight: 500 }}>{t('Tournament:info.prize')}</Typography>
                                            </Stack>
                                            <Stack sx={{
                                                gap: 1,
                                                height: 'calc(100% - 90px)',
                                                overflow: 'auto',
                                                '&::-webkit-scrollbar': {
                                                    width: '0.4em'
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    backgroundColor: '#5A5A5A',
                                                    borderRadius: '4px'
                                                }
                                            }}>
                                                {filteredPlayerList.map((player, index) => (
                                                    <Stack direction="row" key={player.userId} spacing={2} alignItems="center">
                                                        <Typography variant="body2" sx={{
                                                            width: '10%',
                                                            fontSize: '16px',
                                                            textAlign: 'center',
                                                            fontWeight: player.userId === userState.id ? 'bold' : '600',
                                                            color: player.userId === userState.id ? '#ffffff' : '#8695A7'
                                                        }}>{(index + 1).toString()}</Typography>
                                                        <Typography variant="body2" sx={{
                                                            fontSize: '14px',
                                                            fontWeight: '600',
                                                            color: player.userId === userState.id ? '#ffffff' : '#8695A7',
                                                            mr: 'auto'
                                                        }}>
                                                            {renderTournamentCountryFlag(player.countryCode)}
                                                            {player.username} {player.userId === userState.id ? '(You)' : ''}
                                                        </Typography>
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        </>
                                    );
                                    // If no players are registered, show a placeholder
                                    return (
                                        <Box sx={{
                                            textAlign: 'center',
                                            height: '100%',
                                            color: '#8695A7'
                                        }}>
                                            <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.15)', pb: 1, mb: 2 }}>
                                                <Typography variant="body1" sx={{ width: '10%', color: '#8695A7', fontWeight: 500, pl: 1, boxSizing: 'border-box' }}>#</Typography>
                                                <Typography variant="body1" sx={{ width: '40%', color: '#8695A7', fontWeight: 500 }}>{t('Tournament:players.player')}</Typography>
                                                <Typography variant="body1" sx={{ width: '25%', color: '#8695A7', fontWeight: 500 }}>{t('Tournament:players.chips')}</Typography>
                                                <Typography variant="body1" sx={{ width: '25%', color: '#8695A7', fontWeight: 500 }}>{t('Tournament:info.prize')}</Typography>
                                            </Stack>
                                            <Typography variant="body2" sx={{ fontSize: '0.9rem', color: '#8695A7' }}>
                                                {t('Tournament:lobby.noPlayersRegistered')}
                                            </Typography>
                                        </Box>
                                    );
                                })()}
                            </Box>
                        )}
                        {rightTab === 1 && (
                            <Box sx={{ height: 'calc(100% - 53px)' }}>
                                {tournament.activeRooms && tournament.activeRooms.length > 0 && (
                                    <>
                                        <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.15)', pb: 1, mb: 2 }}>
                                            <Typography variant="body1" sx={{ width: '50%', color: '#8695A7', fontWeight: 500 }}>{t('Tournament:tables.table')}</Typography>
                                            <Typography variant="body1" sx={{ width: '20%', color: '#8695A7', fontWeight: 500 }}>{t('Tournament:tables.players')}</Typography>
                                            <Typography variant="body1" sx={{ width: '30%', color: '#8695A7', fontWeight: 500 }}>{t('Tournament:info.openTable')}</Typography>
                                        </Stack>
                                        <Stack sx={{
                                            gap: 1,
                                            mb: 2,
                                            height: 'calc(100% - 49px)',
                                            boxSizing: 'border-box',
                                            overflow: 'auto',
                                            '&::-webkit-scrollbar': {
                                                width: '0.4em'
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                backgroundColor: '#5A5A5A',
                                                borderRadius: '4px'
                                            }
                                        }}>
                                            {tournament.activeRooms.map((room) => (
                                                <Stack direction="row" key={room.roomId} spacing={2} alignItems="center">
                                                    <Typography variant="body2" sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        width: '50%',
                                                        fontSize: '16px',
                                                        fontWeight: '500'
                                                        // fontWeight: isCurrentLevel ? 'bold' : '500',
                                                        // color: isCurrentLevel ? 'white' : '#8695A7'
                                                    }}>{room.isFinalTournamentTable && (<Tooltip title="Final Table" arrow placement="left"><img src={tourneyIcon} alt="Trophy icon" height="16px" style={{ marginRight: '8px'}} /></Tooltip>)}{getTableName(room.tableName)}</Typography>
                                                    <Typography variant="body2" sx={{
                                                        width: '20%',
                                                        fontSize: '16px',
                                                        fontWeight: '500',
                                                        textAlign: 'center'
                                                        // fontWeight: isCurrentLevel ? 'bold' : '500',
                                                        // color: isCurrentLevel ? 'white' : '#8695A7'
                                                    }}>{getTournamentRoomActivePlayerCount(room)}</Typography>
                                                    <Box sx={{ width: '30%', textAlign: 'center' }}>
                                                        <IconButton size="small" sx={{ background: '#63636B', borderRadius: "8px", border: '1px solid #929298' }} onClick={(e) => handleJoinTournamentRoom(room, e)}>
                                                            <ArrowForwardIcon sx={{ fontSize: '16px' }} />
                                                        </IconButton>
                                                    </Box>
                                                </Stack>
                                            ))}
                                        </Stack>
                                        {/* <img src={tablePlaceholder} alt="Table placeholder" style={{ marginTop: 'auto', width: '100%' }} /> */}
                                    </>
                                )}
                            </Box>
                        )}
                    </Stack>
                )}
                {isMobile && (
                    <Stack direction="row" sx={{ position: 'absolute', bottom: '1.5rem', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#1E1F27', border: '1px solid #56575D', borderRadius: '4px', m: '0 auto' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: tab === 0 ? '#FFFFFF' : 'transparent', width: 56, height: 36, borderRadius: '4px' }}>
                            <TrophyIcon sx={{ width: 17, height: 18, color: tab === 0 ? '#2A2B32' : '#8695A6' }} onClick={() => setTab(0)} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: tab === 1 ? '#FFFFFF' : 'transparent', width: 56, height: 36, borderRadius: '4px' }}>
                            <UserIcon sx={{ width: 16, height: 17, color: tab === 1 ? '#2A2B32' : '#8695A6' }} onClick={() => setTab(1)} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: tab === 2 ? '#FFFFFF' : 'transparent', width: 56, height: 36, borderRadius: '4px' }}>
                            <WinIcon sx={{ width: 32, height: 24, color: tab === 2 ? '#2A2B32' : '#8695A6' }} onClick={() => setTab(2)} />
                        </Box>
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};

export default TournamentLobbyContent;
